import { VideoQuality } from '@zoom/videosdk';
import classnames from 'classnames';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ZoomMediaContext from '../../context/media-context';
import ZoomContext from '../../context/zoom-context';
import Avatar from './components/avatar';
import ReportBtn from './components/report-btn';
import ShareView from './components/share-view';
import VideoFooter from './components/video-footer';
import AvatarActionContext from './context/avatar-context';
import { useAvatarAction } from './hooks/useAvatarAction';
import { useActiveVideo } from './hooks/useAvtiveVideo';
import { useNetworkQuality } from './hooks/useNetworkQuality';
import { useParticipantsChange } from './hooks/useParticipantsChange';
import { useSpotlightVideo } from './hooks/useSpotlightVideo';
import { useVideoAspect } from './hooks/useVideoAspectRatio';
import { Radio } from '@mui/material';
import { usePrevious } from '../../hooks/usePrevious';
import { useOrientation } from '../../hooks/useOrientation';

function maxVideoCellWidth(orientation, totalParticipants, spotlighted) {
  return orientation === 'portrait' ? 'none' : `calc(100vw/${Math.min(totalParticipants, spotlighted ? 2 : 4)})`;
}

const VideoContainer = (props) => {
  const {onSessionComplete} = props;
  const zmClient = useContext(ZoomContext);
  const { mediaStream } = useContext(ZoomMediaContext);
  const shareViewRef = useRef(null);
  const videoPlayerListRef = useRef({});
  const [isRecieveSharing, setIsRecieveSharing] = useState(false);
  const [spotlightUsers, setSpotlightUsers] = useState();
  const [participants, setParticipants] = useState(zmClient.getAllUser());
  const [subscribers, setSubscribers] = useState([]);
  const activeVideo = useActiveVideo(zmClient);
  const avatarActionState = useAvatarAction(zmClient, participants, true);
  const networkQuality = useNetworkQuality(zmClient);
  const previousSubscribers = usePrevious(subscribers);
  const aspectRatio = useVideoAspect(zmClient);
  const optionsOfVideoResolution = [
    { label: '720P', value: VideoQuality.Video_720P },
    { label: '360P', value: VideoQuality.Video_360P },
    { label: '180P', value: VideoQuality.Video_180P },
    { label: '90P', value: VideoQuality.Video_90P }
  ];
  const orientation = useOrientation();

  useParticipantsChange(zmClient, (participants) => {
    let pageParticipants = [];
    if (participants.length > 0) {
      if (participants.length === 1) {
        pageParticipants = participants;
      } else {
        pageParticipants = participants
          .filter((user) => user.userId !== zmClient.getSessionInfo().userId)
          .sort((user1, user2) => Number(user2.bVideoOn) - Number(user1.bVideoOn));
        const currentUser = zmClient.getCurrentUserInfo();
        if (currentUser) {
          pageParticipants.splice(1, 0, currentUser);
        }
      }
    }
    setParticipants(pageParticipants);
    setSubscribers(pageParticipants.filter((user) => user.bVideoOn).map((u) => u.userId));
  });
  useSpotlightVideo(zmClient, mediaStream, (p) => {
    setSpotlightUsers(p);
  });
  const setVideoPlayerRef = (userId, element) => {
    if (element) {
      videoPlayerListRef.current[`${userId}`] = element;
    }
  };
  useEffect(() => {
    const addedUsers = subscribers.filter((user) => !(previousSubscribers || []).includes(user));
    const removedUsers = (previousSubscribers || []).filter((user) => !subscribers.includes(user));
    if (removedUsers.length > 0) {
      removedUsers.forEach((userId) => {
        mediaStream?.detachVideo(userId);
      });
    }
    if (addedUsers.length > 0) {
      addedUsers.forEach((userId) => {
        const attachment = videoPlayerListRef.current[`${userId}`];
        if (attachment) {
          mediaStream?.attachVideo(userId, VideoQuality.Video_720P, attachment);
        }
      });
    }
  }, [subscribers, previousSubscribers, mediaStream]);
  const onVideoResolutionChange = useCallback(
    ({ target: { value } }, userId) => {
      const attachment = videoPlayerListRef.current[`${userId}`];
      mediaStream?.attachVideo(userId, value, attachment);
    },
    [videoPlayerListRef, mediaStream]
  );
  const sortedParticipants = useMemo(() => {
    if (spotlightUsers?.length) {
      const splightUserIds = spotlightUsers.map((u) => u.userId);
      return participants
        .filter((user) => !splightUserIds.includes(user.userId))
        .concat(
          participants
            .filter((user) => splightUserIds.includes(user.userId))
            .map((user) => ({ spotlighted: true, ...user }))
        );
    }
    return participants;
  }, [participants, spotlightUsers]);

  return (
    <div className="viewport" style={{ height: 'auto', width: 'auto', minHeight: '100vh' }}>
      <ShareView ref={shareViewRef} onRecieveSharingChange={setIsRecieveSharing} />
      <div
        className={classnames('video-container', 'video-container-attech', {
          'video-container-in-sharing': isRecieveSharing
        })}
      >
        <video-player-container class="video-container-wrap">
          <AvatarActionContext.Provider value={avatarActionState}>
            <ul className="user-list">
              {sortedParticipants.map((user) => {
                const maxWidth = maxVideoCellWidth(orientation, participants.length, (user).spotlighted);
                return (
                  <div
                    className={classnames('video-cell', { 'video-cell-spotlight': (user).spotlighted })}
                    key={user.userId}
                    style={
                      // Bugs in react, aspectRatio doesn't work. https://github.com/facebook/react/issues/21098
                      aspectRatio[`${user.userId}`]
                        ? {
                            aspectRatio: aspectRatio[`${user.userId}`],
                            maxWidth
                          }
                        : {
                            maxWidth
                          }
                    }
                  >
                    {avatarActionState?.avatarActionState[user?.userId]?.videoResolutionAdjust?.toggled && (
                      <div className="change-video-resolution">
                        <Radio.Group
                          options={optionsOfVideoResolution}
                          onChange={(value) => {
                            onVideoResolutionChange(value, user.userId);
                          }}
                          defaultValue={VideoQuality.Video_720P}
                          optionType="button"
                          buttonStyle="solid"
                        />
                      </div>
                    )}
                    {user.bVideoOn && (
                      <div>
                        <video-player
                          class="video-player"
                          ref={(element) => {
                            setVideoPlayerRef(user.userId, element);
                          }}
                        />
                      </div>
                    )}
                    <Avatar
                      participant={user}
                      key={user.userId}
                      isActive={activeVideo === user.userId}
                      networkQuality={networkQuality[`${user.userId}`]}
                    />
                  </div>
                );
              })}
            </ul>
          </AvatarActionContext.Provider>
        </video-player-container>
      </div>
      <VideoFooter className="video-operations" sharing selfShareCanvas={shareViewRef.current?.selfShareRef} onSessionComplete={onSessionComplete}/>
      <ReportBtn />
    </div>
  );
};
export default VideoContainer;
